import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from 'contexts/AuthProvider';
import { ORDER_STAGE_TYPES, VISIBILITY_INDEX_TYPE } from 'helpers/enums';

const VISIBILITY_SCREENS = {
  [VISIBILITY_INDEX_TYPE.ORDER_VIEW]: 'order_item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_ORDER_VIEW]: 'order_special_item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_VIEW]: 'order_special',
  [VISIBILITY_INDEX_TYPE.HALF_HALF_VIEW]: 'order_half_half',
  [VISIBILITY_INDEX_TYPE.HALF_ORDER_VIEW]: 'order_half_item',
  [VISIBILITY_INDEX_TYPE.VOUCHER_VIEW]: 'order_voucher',
  [VISIBILITY_INDEX_TYPE.VOUCHER_ORDER_VIEW]: 'order_voucher_item',
};

const AUTH_SCREENS = {
  [ORDER_STAGE_TYPES.OAUTH_FORM]: 'order_auth',
  [ORDER_STAGE_TYPES.NAME_FORM]: 'order_auth_name',
  [ORDER_STAGE_TYPES.MOBILE_FORM]: 'order_auth_mobile',
  [ORDER_STAGE_TYPES.VERIFY_FROM]: 'order_auth_verify',
};

const STAGE_SCREENS = {
  [ORDER_STAGE_TYPES.REVIEW]: 'order_review',
  [ORDER_STAGE_TYPES.PAYMENT]: 'order_payment',
  [ORDER_STAGE_TYPES.PAYMENT_CARD]: 'order_payment',
};

// Follows the same priority order as getMainComponents() in
// containers/Order/index.js, so the tracked screen always matches what the
// sidebar actually renders.
function getScreenName({
  bookingOpen,
  aboutOpen,
  loyaltyOpen,
  profileOpen,
  visibilityIndex,
  authOrderStage,
  stage,
  hasItems,
}) {
  if (bookingOpen) return 'booking';
  if (aboutOpen) return 'about';
  if (loyaltyOpen) return 'loyalty';
  if (profileOpen) return 'profile';
  if (visibilityIndex && VISIBILITY_SCREENS[visibilityIndex]) {
    return VISIBILITY_SCREENS[visibilityIndex];
  }
  if (AUTH_SCREENS[authOrderStage]) return AUTH_SCREENS[authOrderStage];
  if (stage >= ORDER_STAGE_TYPES.ALL_DONE) return 'home';
  if (STAGE_SCREENS[stage]) return STAGE_SCREENS[stage];
  return hasItems ? 'order_cart' : 'home';
}

function emitScreenView(screenName) {
  if (typeof window === 'undefined') return;

  // GTM — use a Custom Event trigger on `virtual_screen_view` and a Data Layer
  // Variable for `screen_name`.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'virtual_screen_view',
    screen_name: screenName,
  });

  // GA4 direct (gtag.js)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'virtual_screen_view', {
      screen_name: screenName,
    });
  }

  // Meta Pixel direct
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'VirtualScreenView', {
      screen_name: screenName,
    });
  }
}

/**
 * Analytics-only screen tracking for the sidebar SPA flow. Derives a logical
 * screen name from app state and fires a `virtual_screen_view` event to GTM
 * (dataLayer), GA4 (gtag) and Meta Pixel (fbq). Never touches the URL.
 */
export default function useScreenViewTracking({ rightSideViewVisibilityIndex }) {
  const { bookingOpen, aboutOpen, loyaltyOpen, profileOpen, currentOrder } =
    useSelector((state) => state.homeReducer);
  const { authOrderStage } = useAuth();

  const stage = currentOrder?.stage ?? ORDER_STAGE_TYPES.NONE;
  const hasItems = (currentOrder?.menuItems?.length ?? 0) > 0;

  const lastScreen = useRef(null);

  useEffect(() => {
    const screenName = getScreenName({
      bookingOpen,
      aboutOpen,
      loyaltyOpen,
      profileOpen,
      visibilityIndex: rightSideViewVisibilityIndex,
      authOrderStage,
      stage,
      hasItems,
    });

    if (screenName === lastScreen.current) return; // no real change, skip
    lastScreen.current = screenName;

    emitScreenView(screenName);
  }, [
    bookingOpen,
    aboutOpen,
    loyaltyOpen,
    profileOpen,
    rightSideViewVisibilityIndex,
    authOrderStage,
    stage,
    hasItems,
  ]);
}
